import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { classNames } from 'primereact/utils';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { Button, QuoteOutlineIcon } from '@repo/atomic-components';
import {
  selectCaseIdData,
  selectTimelineChronologyDataByStoreKey,
  TimelineChronologyEntry,
  TimelineChronologyType,
  useAppSelector,
} from '@/store';
import { useLazyGetChronologyQuery } from '@/api';
import { AppRoutes, AppRoutesSearchParams } from '@/constants';
import { BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID, useFletchersBroadcast } from '@/broadcast';
import { openNewTab } from '@/utils';

interface ChronologyEntryPanelProps {
  /** Page ID currently in the viewport — the popover shows entries that
   *  reference this page. */
  pageId: string | null | undefined;
  /** Authoritative count of chronology entries attached to the current
   *  page, sourced from the same place the thumbnail indicator uses
   *  (pageInfoCache with a fallback to the bundle data). Drives the
   *  badge directly and, when it changes (e.g. user just added an
   *  entry via the dialog), triggers a chronology re-fetch so the
   *  popover row list stays in sync. */
  entryCount: number;
  /** Fires the existing Add-to-Chronology flow (opens the dialog with the
   *  current page pre-selected). */
  onAddNewEntry: () => void;
}

const buttonBase = '!h-[32px] !w-[32px] !p-0 justify-center hover:!bg-gray-50 !text-zinc-950';
const hideLabel = { label: { className: '!hidden' } };
// Match the tooltip styling of the zoom-rail so hovers baseline-align with
// the 32px-tall buttons on the same vertical strip.
const railTooltipOptions = {
  position: 'left' as const,
  showDelay: 200,
  at: 'left-8 top',
  my: 'right top',
  className: 'zoom-rail-tooltip',
};

interface ChronologyPopoverProps {
  buttonRect: DOMRect;
  entries: TimelineChronologyEntry[];
  onEntryClick: (entryId: string) => void;
  onAddNewEntry: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const ChronologyPopover: React.FC<ChronologyPopoverProps> = ({
  buttonRect,
  entries,
  onEntryClick,
  onAddNewEntry,
  onMouseEnter,
  onMouseLeave,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const MARGIN = 8;

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width || 380;
    const height = rect.height || 200;
    const buttonCentreY = buttonRect.top + buttonRect.height / 2;
    const idealTop = buttonCentreY - height / 2;
    const viewportH = window.innerHeight;
    const maxTop = Math.max(MARGIN, viewportH - height - MARGIN);
    const top = Math.min(Math.max(MARGIN, idealTop), maxTop);
    const left = buttonRect.left - MARGIN - width;
    setPos({ top, left });
  }, [buttonRect, entries.length]);

  const count = entries.length;

  return createPortal(
    <div
      className="fixed z-[1000]"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        ref={cardRef}
        className={classNames(
          'border-fletchers-gray-110 flex max-h-[400px] w-[380px] flex-col overflow-hidden rounded-[8px] border bg-white',
          'shadow-[0px_4px_12px_0px_#0000001f]',
        )}
      >
        <div className="border-fletchers-gray-110 flex items-center border-b px-6 py-4">
          <div className="text-[14px] font-semibold text-black">
            {count} associated chronology {count === 1 ? 'entry' : 'entries'}
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto px-6 pt-2 pb-2">
          {entries.map((entry) => {
            const dateLabel = entry.date ? format(new Date(entry.date), 'd MMM y') : 'No date';
            const timeLabel = entry.time ? ` ${entry.time.substring(0, 5)}` : '';
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => onEntryClick(entry.id)}
                className={classNames(
                  'group -mx-6 flex w-[calc(100%+48px)] cursor-pointer items-start gap-3 px-6 py-3 text-left',
                  'hover:bg-fletchers-gray-50 transition-colors',
                )}
              >
                <QuoteOutlineIcon width={17} className="text-fletchers-gray-150 mt-1 shrink-0" />
                <span className="min-w-0 flex-1">
                  {/* Date + optional time + optional provider on one row.
                      Flex layout so the provider (grow-shrink) can absorb
                      the remaining width and truncate with ellipsis; the
                      date column stays crisp at its natural size (shrink-0). */}
                  <span className="flex min-w-0 items-baseline gap-2 text-[14px] font-semibold text-black">
                    <span className="shrink-0">
                      {dateLabel}
                      {timeLabel}
                    </span>
                    {entry.providerName ? (
                      <span className="text-fletchers-gray-200 min-w-0 flex-1 truncate font-normal">
                        {entry.providerName}
                      </span>
                    ) : null}
                  </span>
                  {entry.summary && (
                    <span className="mt-0.5 line-clamp-2 text-[14px] leading-5 font-normal text-black">
                      {entry.summary}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onAddNewEntry}
          className={classNames(
            'border-fletchers-gray-110 hover:bg-fletchers-gray-50 flex cursor-pointer items-center gap-2 border-t px-6 py-3 text-left text-[14px] font-semibold text-black transition-colors',
          )}
        >
          <i className="pi pi-plus !text-[12px] text-black" />
          Add to Chronology
        </button>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Chronology-entry button on the PDF rail. Behaviour:
 *
 *   0 entries on this page → click fires `onAddNewEntry` (opens the
 *     existing Add-to-Chronology dialog). No popover.
 *   ≥1 entry              → hover *or* click opens a popover listing the
 *     entries with date/provider/summary. Each row jumps to that entry on
 *     the Chronology tab (broadcast to an open tab if one exists, else
 *     open a new tab pointed at the entry). Popover also has an
 *     "+ Add a new entry" row at the bottom.
 *
 * Data source: on mount (or when we know we have entries) we fetch the
 * case's chronology with the CHRONOLOGY store key so it doesn't overwrite
 * the timeline slice's data. This mirrors the pattern used by
 * `useTimelineChronologySlotIn`.
 */
export const ChronologyEntryPanel: React.FC<ChronologyEntryPanelProps> = ({ pageId, entryCount, onAddNewEntry }) => {
  const { caseId: caseIdFromUrl } = useParams();
  const caseId = useAppSelector(selectCaseIdData) ?? caseIdFromUrl ?? null;
  const chronologyData = useAppSelector(selectTimelineChronologyDataByStoreKey(TimelineChronologyType.CHRONOLOGY));
  const { sendMessageWithResponse } = useFletchersBroadcast();

  const [triggerGetChronology, { isFetching }] = useLazyGetChronologyQuery();

  // Fetch (or re-fetch) the chronology when the authoritative entry-count
  // changes and the new value is > 0. Deliberately does NOT depend on
  // `pageId`: page-flipping between two pages with the same entryCount
  // wouldn't produce different data, and we don't want to hammer the
  // endpoint with identical requests just because the viewport moved.
  // What we do want to react to:
  //   - initial mount on a page that has entries (entryCount 0 → N)
  //   - flip to a page whose count differs from the previous one
  //   - user adding a new entry via the dialog (count bumps thanks to
  //     the optimistic cache update in AddToChronology.tsx) — this is
  //     the one that was broken before we relied on entryCount.
  // We can't just rely on the initial Redux slice because the create
  // endpoint doesn't invalidate it.
  useEffect(() => {
    if (!caseId) return;
    if (entryCount <= 0) return;
    triggerGetChronology({ caseId, timelineChronologyKey: TimelineChronologyType.CHRONOLOGY });
  }, [caseId, entryCount, triggerGetChronology]);

  // Entries whose `pages[].pageId` includes the current page.
  const entriesForCurrentPage = useMemo<TimelineChronologyEntry[]>(() => {
    if (!pageId || !chronologyData?.entries) return [];
    const result: TimelineChronologyEntry[] = [];
    for (const entry of chronologyData.entries) {
      if (!entry || typeof entry !== 'object') continue;
      if (!('id' in entry)) continue;
      if (entry.pages?.some((page) => page.pageId === pageId)) {
        result.push(entry);
      }
    }
    return result;
  }, [chronologyData, pageId]);

  const buttonRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    [],
  );

  // Close the popover when the current page changes.
  useEffect(() => {
    setIsOpen(false);
  }, [pageId]);

  // Badge count is the parent-supplied `entryCount` (authoritative — same
  // source as the thumbnail indicator, includes optimistic updates from
  // AddToChronology). The `entriesForCurrentPage.length` we derive from
  // the fetched chronology slice is only used to gate the popover
  // content, since we can't render date/provider/summary rows before
  // that data has actually landed.
  const count = entryCount;
  const showAsActive = count > 0;
  const canShowPopover = entriesForCurrentPage.length > 0;

  const handleMouseEnter = () => {
    if (!canShowPopover) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    openTimeoutRef.current = setTimeout(() => setIsOpen(true), 150);
  };
  const handleMouseLeave = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };
  const handleModalMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const handleModalMouseLeave = () => setIsOpen(false);

  const handleButtonClick = () => {
    // When the current page has no entries, click behaves like the old
    // chronology button — go straight to the add dialog. Otherwise
    // mirror the hover: open the popover. If we know entries exist
    // (count > 0) but the fetched slice isn't ready yet (`isFetching`)
    // we still open — the popover renders a small loading state until
    // rows arrive.
    if (count === 0 && !isFetching) {
      onAddNewEntry();
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleAddNewEntryFromPopover = () => {
    setIsOpen(false);
    onAddNewEntry();
  };

  const handleEntryClick = async (entryId: string) => {
    setIsOpen(false);
    // Ping any open chronology tab first; if one responds, it handles the
    // scroll and refocuses itself. Otherwise open a fresh tab with the
    // entry id encoded so the newly-mounted TimelineChronologyPage
    // performs the scroll.
    const response = await sendMessageWithResponse<{ received: boolean }>(
      BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID,
      { chronologyEntryId: entryId },
      2000,
    ).catch(() => {
      if (!caseId) return null;
      const url =
        `/${AppRoutes.CaseList}` +
        `/${caseId}` +
        `/${AppRoutes.AiProcessedDocuments}` +
        `/${AppRoutes.Chronology}` +
        `/${caseId}` +
        `?${AppRoutesSearchParams.chronologyEntryIdOnInit}=${entryId}`;
      return openNewTab(url);
    });
    if (response instanceof Window) {
      try {
        response.focus();
      } catch {
        // ignore
      }
    }
  };

  const buttonRect = buttonRef.current?.getBoundingClientRect();

  return (
    <div ref={buttonRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Button
        text
        icon={
          <span className="relative inline-flex">
            <QuoteOutlineIcon width={17} className={showAsActive ? 'text-fletchers-teal-500' : undefined} />
            {count > 0 && (
              <svg
                width="17"
                height="14"
                viewBox="0 0 191 157"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
                overflow="visible"
                className="pointer-events-none absolute inset-0"
              >
                {/* Halo-outlined number so it punches through the
                    quotation marks behind. Size/stroke are calibrated
                    to match the 12px-fontSize digits on the duplicates
                    button below. */}
                <text
                  x="175"
                  y="180"
                  textAnchor="middle"
                  fontSize="134"
                  fontWeight="600"
                  stroke="white"
                  strokeWidth="39"
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fill="var(--color-fletchers-teal-500, #009684)"
                  style={{ fontFamily: 'inherit' }}
                >
                  {count > 99 ? '99+' : `${count}`}
                </text>
              </svg>
            )}
          </span>
        }
        aria-label={count > 0 ? `View ${count} chronology ${count === 1 ? 'entry' : 'entries'}` : 'Add to Chronology'}
        tooltip={count === 0 ? 'Add to Chronology' : undefined}
        tooltipOptions={railTooltipOptions}
        className={buttonBase}
        pt={hideLabel}
        onClick={handleButtonClick}
      />
      {isOpen && buttonRect && entriesForCurrentPage.length > 0 && (
        <ChronologyPopover
          buttonRect={buttonRect}
          entries={entriesForCurrentPage}
          onEntryClick={handleEntryClick}
          onAddNewEntry={handleAddNewEntryFromPopover}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
        />
      )}
    </div>
  );
};
